import { useMemo } from 'react';
import type { IRecordTypeDefinition } from '@platform/core/types';
import { findModuleDefinition } from '@platform/modularity/moduleDefinitionRegistry';
import { useGlobalRecordTypeOverrideQuery, useRecordTypeOverrideQuery } from '@platform/state/api';
import type { FormSourceKind } from './form/FormSourceKind';
import { formModelOf } from './form/formSummary';
import { hasForms, scopeFormOf } from './form/scopeForm';

/**
 * Звідки форма типу записів для пігулки «Записів і форм» (прототип v4 cdForms state): на рівні «Глобально» — глобальна форма (зелена
 * «Глобально»), якщо глобальний патч несе форму, інакше «Стандартний»; у просторі — власна форма простору, успадкована глобальна форма чи
 * стандартний макет (scopeFormOf за патчами простору й глобальним), доки патч простору вантажиться — за злитим визначенням.
 */
export function useFormSource(ws: string, moduleKey: string, rt: IRecordTypeDefinition | undefined, global: boolean): FormSourceKind {
  const key = rt?.key ?? '';
  const own = useRecordTypeOverrideQuery({ ws, rt: key }, { skip: global || !ws || !key });
  const inherited = useGlobalRecordTypeOverrideQuery({ rt: key }, { skip: !key });
  return useMemo(() => {
    if (!rt) return 'std';
    if (global) return hasForms(inherited.data?.patch) ? 'global' : 'std';
    const published = findModuleDefinition(moduleKey)?.recordTypes.find((r) => r.key === rt.key);
    if (!own.data) return formModelOf(rt, published).source;
    return scopeFormOf(rt, own.data.patch, published, { global: false, inherited: inherited.data?.patch }).source;
  }, [rt, global, inherited.data, own.data, moduleKey]);
}
